import { KeyboardEventHandler, MouseEvent } from 'react';
import { UnstyledButton } from '@ardoq/button';
import { ArdoqIconCategory, CloseIcon, IconSize } from '@ardoq/icons';
import { colors, space } from '@ardoq/design-tokens';
import styled from 'styled-components';
import { typographyMixins } from '@ardoq/typography';
import { TextOverflow } from '@ardoq/popovers';
import {
  EntityIcon,
  ComponentDecoratorAsTagProps,
  ComponentTagContainerProps,
  ComponentTypeLabel,
  componentTagContainerCSS,
} from '@ardoq/basic-components';

export type InteractiveComponentDecoratorAsTagProps =
  ComponentDecoratorAsTagProps & {
    onClick: VoidFunction;
    onCloseClick?: VoidFunction;
    onKeyDown?: KeyboardEventHandler<HTMLElement>;
    title?: string;
  };

/**
 * Clicking on the component name triggers onClick
 * If you provide "onCloseClick" callback, the "x" button will be rendered -
 * meant for removing references in Inventory.
 */
export const InteractiveComponentDecoratorAsTag = ({
  shadedColor,
  lightenedColor,
  contrastColor,
  icon,
  value,
  isImage,
  label,
  isSelected = false,
  noMargin = false,
  onClick,
  onCloseClick,
  onKeyDown,
  title,
}: InteractiveComponentDecoratorAsTagProps) => {
  const handleCloseClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onCloseClick?.();
  };

  return (
    <Container
      $borderColor={shadedColor}
      $backgroundColor={lightenedColor}
      $isSelected={isSelected}
      $noMargin={noMargin}
    >
      <MainButton type="button" onClick={onClick} onKeyDown={onKeyDown}>
        <EntityIcon
          imageSrc={isImage ? value : undefined}
          iconId={
            icon.category === ArdoqIconCategory.FontAwesome || icon.isSVG
              ? icon.id
              : undefined
          }
          iconSize={IconSize.SMALL}
          color={contrastColor}
        />

        {label ? (
          <LabelOverflow lineClamp={1} renderIn="tooltip" textContent={title}>
            <ComponentTypeLabel $color={contrastColor}>
              {label}
            </ComponentTypeLabel>
          </LabelOverflow>
        ) : null}
      </MainButton>

      {onCloseClick ? (
        <CloseButton
          type="button"
          aria-label={`Remove ${label}`}
          onClick={handleCloseClick}
          onMouseDownCapture={event => event.stopPropagation()}
        >
          <CloseIcon iconSize={IconSize.SMALL} />
        </CloseButton>
      ) : null}
    </Container>
  );
};

const Container = styled.div<ComponentTagContainerProps>`
  ${componentTagContainerCSS}
`;

const MainButton = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  gap: ${space.xxsmall};
  min-width: 0;
  flex: 1 1 auto;
  text-align: left;
`;

const LabelOverflow = styled(TextOverflow)`
  ${typographyMixins.caption}
`;

const CloseButton = styled(UnstyledButton)`
  display: inline-flex;
  align-items: center;
  color: ${colors.iconAction};
  &:hover {
    color: ${colors.iconActionHover};
  }
`;
